import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useIsFetching, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowDown, ArrowUp, BarChart3, Car, DollarSign, FileText, Flame, ImageOff, Link2, Mail, MessageCircle, Minus, MessageSquare, Receipt, RefreshCw, Target, TrendingUp } from "lucide-react";
import { ManagerDashboard } from "@/components/admin/ManagerDashboard";

const dkkFormatter = new Intl.NumberFormat("da-DK", {
  style: "currency",
  currency: "DKK",
  maximumFractionDigits: 0,
});

const periodSchema = z.object({
  period: fallback(z.union([z.literal(7), z.literal(30), z.literal(90)]), 30).default(30),
  as: fallback(z.literal("manager").optional(), undefined).optional(),
});

type ChartPeriod = 7 | 30 | 90;
type PeriodSearch = z.infer<typeof periodSchema>;

const PERIOD_STORAGE_KEY = "admin-dashboard-period";
const AUTO_REFRESH_MS = 60_000;
const isValidPeriod = (v: unknown): v is ChartPeriod => v === 7 || v === 30 || v === 90;

export const Route = createFileRoute("/admin/")({
  validateSearch: zodValidator(periodSchema),
  component: AdminDashboard,
});

function AdminDashboard() {
  const { period: chartPeriod, as: previewAs } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/" });
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ predicate: (q) => String(q.queryKey[0]).startsWith("admin-") }) > 0;

  // Bestem rolle: Manager (moderator) får et tilpasset dashboard
  const { data: roleInfo } = useQuery({
    queryKey: ["dashboard-user-role"],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) return { isModeratorOnly: false, isAdmin: false };
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .in("role", ["super_admin", "admin", "moderator"]);
      const roles = (data ?? []).map((r) => r.role);
      const isAdmin = roles.includes("admin") || roles.includes("super_admin");
      const isModeratorOnly =
        roles.includes("moderator") && !isAdmin;
      return { isModeratorOnly, isAdmin };
    },
    staleTime: 60_000,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("admin-") });
  };
  const handleCopyLink = async () => {
    if (typeof window === "undefined") return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link kopieret", {
        description: `Periode: sidste ${chartPeriod} dage`,
      });
    } catch {
      toast.error("Kunne ikke kopiere link", {
        description: "Browseren tillod ikke adgang til udklipsholderen",
      });
    }
  };
  const hasHydratedRef = useRef(false);

  // On first mount: if URL has no explicit ?period=, restore from localStorage
  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const urlHasPeriod = url.searchParams.has("period");
    if (urlHasPeriod) return;

    const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
    const parsed = stored ? Number(stored) : NaN;
    if (isValidPeriod(parsed) && parsed !== chartPeriod) {
      navigate({ search: (prev: PeriodSearch) => ({ ...prev, period: parsed }), replace: true });
    }
  }, [chartPeriod, navigate]);

  const setChartPeriod = (period: ChartPeriod) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PERIOD_STORAGE_KEY, String(period));
    }
    navigate({ search: (prev: PeriodSearch) => ({ ...prev, period }), replace: true });
  };
  const { data: carCount } = useQuery({
    queryKey: ["admin-car-count"],
    queryFn: async () => {
      const { data, error } = await supabase.from("cars").select("id");
      if (error) {
        console.error("admin-car-count failed:", error);
        return 0;
      }
      return data?.length ?? 0;
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: activeCarCount } = useQuery({
    queryKey: ["admin-active-car-count"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cars")
        .select("id")
        .eq("status", "active");
      if (error) {
        console.error("admin-active-car-count failed:", error);
        return 0;
      }
      return data?.length ?? 0;
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // Email health: deduplicated failure rate last 24h (failed / dlq / bounced / complained vs sent)
  const { data: emailHealth } = useQuery({
    queryKey: ["admin-email-health-24h"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("email_send_log")
        .select("message_id,status,created_at")
        .gte("created_at", since)
        .not("message_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      // Dedupe: keep latest status per message_id
      const latest = new Map<string, string>();
      for (const row of data ?? []) {
        const mid = row.message_id as string;
        if (!latest.has(mid)) latest.set(mid, row.status);
      }
      const statuses = Array.from(latest.values());
      const total = statuses.length;
      const failed = statuses.filter((s) =>
        ["dlq", "failed", "bounced", "complained"].includes(s),
      ).length;
      const sent = statuses.filter((s) => s === "sent").length;
      const denom = sent + failed;
      const rate = denom === 0 ? 0 : (failed / denom) * 100;
      return { total, failed, sent, rate };
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: blogCount } = useQuery({
    queryKey: ["admin-blog-count"],
    queryFn: async () => {
      const { data, error } = await supabase.from("blog_posts").select("id");
      if (error) {
        console.error("admin-blog-count failed:", error);
        return 0;
      }
      return data?.length ?? 0;
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: messageCount } = useQuery({
    queryKey: ["admin-message-count"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("id")
        .eq("is_read", false);
      if (error) {
        console.error("admin-message-count failed:", error);
        return 0;
      }
      return data?.length ?? 0;
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // Active or draft cars without any uploaded images — surfaces incomplete
  // listings prominently on the dashboard so admins fix them quickly.
  const { data: carsWithoutImagesCount } = useQuery({
    queryKey: ["admin-cars-without-images-count"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cars")
        .select("id")
        .neq("status", "sold")
        .or("images.is.null,images.eq.{}");
      if (error) {
        console.error("admin-cars-without-images-count failed:", error);
        return 0;
      }
      return data?.length ?? 0;
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: topInquiredCars, isLoading: topLoading } = useQuery({
    queryKey: ["admin-top-inquired-cars", chartPeriod],
    queryFn: async () => {
      const since = new Date(Date.now() - chartPeriod * 24 * 60 * 60 * 1000).toISOString();
      const { data: msgs } = await supabase
        .from("contact_messages")
        .select("car_id")
        .not("car_id", "is", null)
        .gte("created_at", since);

      const counts = new Map<string, number>();
      for (const m of msgs ?? []) {
        if (!m.car_id) continue;
        counts.set(m.car_id, (counts.get(m.car_id) ?? 0) + 1);
      }
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
      if (top.length === 0) return [];

      const { data: cars } = await supabase
        .from("cars")
        .select("id, brand, model, variant, year, images, seo_slug")
        .in("id", top.map(([id]) => id));

      return top
        .map(([id, count]) => {
          const car = cars?.find((c) => c.id === id);
          return car ? { ...car, count } : null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: inquiriesPerDay, dataUpdatedAt: inquiriesUpdatedAt } = useQuery({
    queryKey: ["admin-inquiries-per-day", chartPeriod],
    queryFn: async () => {
      const days = chartPeriod;
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - (days - 1));

      const { data: msgs } = await supabase
        .from("contact_messages")
        .select("created_at")
        .gte("created_at", start.toISOString());

      const counts = new Map<string, number>();
      for (let i = 0; i < days; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        counts.set(d.toISOString().slice(0, 10), 0);
      }
      for (const m of msgs ?? []) {
        const key = new Date(m.created_at).toISOString().slice(0, 10);
        if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      return [...counts.entries()].map(([date, count]) => ({ date, count }));
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // === Handelsværdi pr. dag/uge (mini-graf) ===
  // 7d/30d: daglig granularitet. 90d: ugentlig (~13 søjler) for læsbarhed.
  const { data: salesPerBucket } = useQuery({
    queryKey: ["admin-sales-per-bucket", chartPeriod],
    queryFn: async () => {
      const granularity: "day" | "week" = chartPeriod === 90 ? "week" : "day";
      const bucketDays = granularity === "week" ? 7 : 1;
      const buckets = granularity === "week" ? Math.ceil(chartPeriod / 7) : chartPeriod;

      const start = new Date();
      start.setHours(0, 0, 0, 0);
      if (granularity === "week") {
        const dow = (start.getDay() + 6) % 7; // mandag=0
        start.setDate(start.getDate() - dow - (buckets - 1) * 7);
      } else {
        start.setDate(start.getDate() - (buckets - 1));
      }

      const { data: rows } = await supabase
        .from("cars")
        .select("price, updated_at")
        .eq("status", "sold")
        .gte("updated_at", start.toISOString());

      const sums = new Map<string, { value: number; count: number }>();
      for (let i = 0; i < buckets; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i * bucketDays);
        sums.set(d.toISOString().slice(0, 10), { value: 0, count: 0 });
      }

      const keys = [...sums.keys()].sort();
      for (const r of rows ?? []) {
        if (!r.updated_at) continue;
        const sold = new Date(r.updated_at);
        sold.setHours(0, 0, 0, 0);
        let bucketKey: string | null = null;
        if (granularity === "day") {
          bucketKey = sold.toISOString().slice(0, 10);
        } else {
          const soldIso = sold.toISOString().slice(0, 10);
          for (let i = keys.length - 1; i >= 0; i--) {
            if (keys[i] <= soldIso) {
              bucketKey = keys[i];
              break;
            }
          }
        }
        if (bucketKey && sums.has(bucketKey)) {
          const cur = sums.get(bucketKey)!;
          cur.value += r.price ?? 0;
          cur.count += 1;
        }
      }

      return [...sums.entries()].map(([date, v]) => ({
        date,
        value: v.value,
        count: v.count,
        granularity,
      }));
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const { data: chatConversion } = useQuery({
    queryKey: ["admin-chat-conversion", chartPeriod],
    queryFn: async () => {
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      const currentSince = new Date(now - chartPeriod * day).toISOString();
      const previousSince = new Date(now - 2 * chartPeriod * day).toISOString();

      const { data: convos } = await supabase
        .from("chat_conversations")
        .select("id, created_at")
        .gte("created_at", previousSince);

      const all = convos ?? [];
      const empty = { total: 0, bookings: 0, rate: 0, prevRate: 0, delta: null as number | null };
      if (all.length === 0) return empty;

      const ids = all.map((c) => c.id);
      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("conversation_id, content")
        .in("conversation_id", ids)
        .eq("role", "assistant");

      const booked = new Set<string>();
      for (const m of msgs ?? []) {
        if (m.content.startsWith("✅") && m.content.includes("prøvekørsel")) {
          booked.add(m.conversation_id);
        }
      }

      const currentConvos = all.filter((c) => c.created_at >= currentSince);
      const previousConvos = all.filter(
        (c) => c.created_at >= previousSince && c.created_at < currentSince,
      );

      const currentBookings = currentConvos.filter((c) => booked.has(c.id)).length;
      const previousBookings = previousConvos.filter((c) => booked.has(c.id)).length;

      const rate = currentConvos.length > 0 ? (currentBookings / currentConvos.length) * 100 : 0;
      const prevRate = previousConvos.length > 0 ? (previousBookings / previousConvos.length) * 100 : 0;
      const delta: number | null = previousConvos.length === 0 ? null : rate - prevRate;

      return {
        total: currentConvos.length,
        bookings: currentBookings,
        rate,
        prevRate,
        delta,
      };
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // === Manager-KPI: Leads i perioden (med sammenligning) ===
  const { data: leadsKpi } = useQuery({
    queryKey: ["admin-leads-kpi", chartPeriod],
    queryFn: async () => {
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      const currentSince = new Date(now - chartPeriod * day).toISOString();
      const previousSince = new Date(now - 2 * chartPeriod * day).toISOString();
      const { data: currentRows } = await supabase
        .from("contact_messages")
        .select("id")
        .gte("created_at", currentSince);
      const { data: previousRows } = await supabase
        .from("contact_messages")
        .select("id")
        .gte("created_at", previousSince)
        .lt("created_at", currentSince);
      const cur = currentRows?.length ?? 0;
      const prev = previousRows?.length ?? 0;
      const delta = prev === 0 ? null : ((cur - prev) / prev) * 100;
      return { current: cur, previous: prev, delta };
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // === Manager-KPI: Solgte biler — sum, antal, gennemsnit, median ===
  const { data: salesKpi } = useQuery({
    queryKey: ["admin-sales-kpi", chartPeriod],
    queryFn: async () => {
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      const currentSince = new Date(now - chartPeriod * day).toISOString();
      const previousSince = new Date(now - 2 * chartPeriod * day).toISOString();

      const { data: currentRows } = await supabase
        .from("cars")
        .select("price, updated_at")
        .eq("status", "sold")
        .gte("updated_at", currentSince);
      const { data: previousRows } = await supabase
        .from("cars")
        .select("price, updated_at")
        .eq("status", "sold")
        .gte("updated_at", previousSince)
        .lt("updated_at", currentSince);

      const sumValue = (rows: { price: number | null }[] | null) =>
        (rows ?? []).reduce((s, r) => s + (r.price ?? 0), 0);
      const medianValue = (rows: { price: number | null }[] | null): number => {
        const vals = (rows ?? [])
          .map((r) => r.price ?? 0)
          .filter((v) => v > 0)
          .sort((a, b) => a - b);
        if (vals.length === 0) return 0;
        const mid = Math.floor(vals.length / 2);
        return vals.length % 2 === 0 ? (vals[mid - 1] + vals[mid]) / 2 : vals[mid];
      };

      const current = sumValue(currentRows);
      const previous = sumValue(previousRows);
      const count = (currentRows ?? []).length;
      const delta = previous === 0 ? null : ((current - previous) / previous) * 100;
      const median = medianValue(currentRows);
      const previousMedian = medianValue(previousRows);
      const medianDelta =
        previousMedian === 0 ? null : ((median - previousMedian) / previousMedian) * 100;
      return { current, previous, count, delta, median, previousMedian, medianDelta };
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });


  // === Manager-KPI: Chat-samtaler i perioden + konvertering ===
  const { data: chatKpi } = useQuery({
    queryKey: ["admin-chat-kpi", chartPeriod],
    queryFn: async () => {
      const since = new Date(Date.now() - chartPeriod * 24 * 60 * 60 * 1000).toISOString();
      const { data: convos } = await supabase
        .from("chat_conversations")
        .select("id, created_at")
        .gte("created_at", since);
      const all = convos ?? [];
      if (all.length === 0) return { total: 0, bookings: 0, rate: 0 };
      const ids = all.map((c) => c.id);
      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("conversation_id, content")
        .in("conversation_id", ids)
        .eq("role", "assistant");
      const booked = new Set<string>();
      for (const m of msgs ?? []) {
        if (m.content.startsWith("✅") && m.content.includes("prøvekørsel")) {
          booked.add(m.conversation_id);
        }
      }
      const bookings = all.filter((c) => booked.has(c.id)).length;
      const rate = (bookings / all.length) * 100;
      return { total: all.length, bookings, rate };
    },
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const renderDelta = (delta: number | null | undefined, suffix = "%") => {
    if (delta === null || delta === undefined) {
      return <span className="text-muted-foreground">— ingen sammenligning</span>;
    }
    const Icon = delta > 0.5 ? ArrowUp : delta < -0.5 ? ArrowDown : Minus;
    const cls =
      delta > 0.5
        ? "bg-green-500/15 text-green-600"
        : delta < -0.5
          ? "bg-destructive/15 text-destructive"
          : "bg-muted text-muted-foreground";
    return (
      <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium ${cls}`}>
        <Icon className="h-3 w-3" />
        {delta > 0 ? "+" : ""}
        {delta.toFixed(1)}
        {suffix} vs. forrige
      </span>
    );
  };

  // Track baseline unread count from page load to show "new since" badge
  const baselineMessageCountRef = useRef<number | null>(null);
  if (messageCount !== undefined && baselineMessageCountRef.current === null) {
    baselineMessageCountRef.current = messageCount;
  }
  const newMessages =
    messageCount !== undefined && baselineMessageCountRef.current !== null
      ? Math.max(0, messageCount - baselineMessageCountRef.current)
      : 0;

  const stats = [
    { label: "Biler i alt", value: carCount ?? 0, icon: Car, color: "bg-primary/10 text-primary", badge: 0 },
    { label: "Aktive biler", value: activeCarCount ?? 0, icon: TrendingUp, color: "bg-green-500/10 text-green-600", badge: 0 },
    { label: "Blogindlæg", value: blogCount ?? 0, icon: FileText, color: "bg-accent/10 text-accent", badge: 0 },
    { label: "Ulæste beskeder", value: messageCount ?? 0, icon: MessageSquare, color: "bg-orange-500/10 text-orange-600", badge: newMessages },
  ];

  // Manager (moderator-only) får et fokuseret KPI-dashboard.
  // Admins kan også preview ManagerDashboard via ?as=manager (toggle nederst på siden).
  const showManagerView = roleInfo?.isModeratorOnly || (roleInfo?.isAdmin && previewAs === "manager");
  if (showManagerView) {
    return (
      <div>
        {roleInfo?.isAdmin && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs">
            <span className="font-medium text-primary">
              👁 Preview: ser dashboardet som en Manager (moderator)
            </span>
            <button
              type="button"
              onClick={() =>
                navigate({
                  search: (prev: PeriodSearch) => ({ ...prev, as: undefined }),
                  replace: true,
                })
              }
              className="rounded-md bg-card px-2.5 py-1 font-medium text-foreground ring-1 ring-border transition-colors hover:bg-muted"
            >
              Skift tilbage til Admin-view
            </button>
          </div>
        )}
        <ManagerDashboard
          period={chartPeriod}
          onPeriodChange={(p) => setChartPeriod(p)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Velkommen til AutoXChange administration</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {emailHealth && emailHealth.total > 0 && (() => {
            const isHealthy = emailHealth.rate < 5;
            const isWarning = emailHealth.rate >= 5 && emailHealth.rate < 15;
            const cls = isHealthy
              ? "border-green-500/30 bg-green-500/10 text-green-700 hover:bg-green-500/15"
              : isWarning
                ? "border-orange-500/30 bg-orange-500/10 text-orange-700 hover:bg-orange-500/15"
                : "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15";
            return (
              <Link
                to="/admin/emails"
                title={`${emailHealth.failed} fejlet / ${emailHealth.sent} sendt af ${emailHealth.total} unikke emails sidste 24t`}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium shadow-sm transition-colors ${cls}`}
              >
                <Mail className="h-3.5 w-3.5" />
                <span>Email-fejlrate 24t: {emailHealth.rate.toFixed(1)}%</span>
                <span className="text-[10px] opacity-70">({emailHealth.failed}/{emailHealth.sent + emailHealth.failed})</span>
              </Link>
            );
          })()}
          {roleInfo?.isAdmin && (
            <button
              type="button"
              onClick={() =>
                navigate({
                  search: (prev: PeriodSearch) => ({ ...prev, as: "manager" }),
                  replace: true,
                })
              }
              title="Se hvordan dashboardet ser ud for en Manager (moderator-rolle)"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
            >
              👁 Se som Manager
            </button>
          )}
        </div>
      </div>


      {carsWithoutImagesCount !== undefined && carsWithoutImagesCount > 0 && (
        <Link
          to="/admin/biler"
          search={{ images: "without" } as never}
          className="mt-4 flex items-center gap-4 rounded-xl bg-gradient-to-r from-destructive/15 via-destructive/10 to-destructive/5 p-5 ring-1 ring-destructive/30 transition hover:ring-destructive/60"
        >
          <div className="rounded-lg bg-destructive/20 p-3 text-destructive">
            <ImageOff className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-destructive">
              <AlertTriangle className="h-3 w-3" /> Mangler billeder
            </p>
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-sm text-card-foreground">
              <span className="text-3xl font-bold text-destructive">{carsWithoutImagesCount}</span>
              <span className="text-muted-foreground">
                {carsWithoutImagesCount === 1 ? "bil mangler fotos" : "biler mangler fotos"} — kunder ser et tomt fallback-kort
              </span>
            </p>
          </div>
          <span className="hidden shrink-0 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground sm:inline">
            Upload nu →
          </span>
        </Link>
      )}

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl bg-card p-6 shadow-sm ring-1 ring-border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">{stat.label}</span>
              <div className={`rounded-lg p-2 ${stat.color}`}>
                <stat.icon className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <p className="text-3xl font-bold text-card-foreground">{stat.value}</p>
              {stat.badge > 0 && (
                <span
                  className="inline-flex items-center rounded-full bg-orange-500/15 px-2 py-0.5 text-xs font-semibold text-orange-600"
                  title="Nye siden du åbnede dashboardet"
                >
                  +{stat.badge} nye
                </span>
              )}
            </div>
          </div>
        ))}
      </div>


      {/* Manager-KPI grid — leads, chat, gns./median handelsværdi */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/admin/beskeder"
          className="rounded-xl bg-card p-5 shadow-sm ring-1 ring-border transition hover:ring-primary/40"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Leads</span>
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <Target className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-3 text-3xl font-bold text-card-foreground">{leadsKpi?.current ?? 0}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Forrige periode: {leadsKpi?.previous ?? 0}
          </p>
          <div className="mt-2">{renderDelta(leadsKpi?.delta)}</div>
        </Link>

        <Link
          to="/admin/chat"
          className="rounded-xl bg-card p-5 shadow-sm ring-1 ring-border transition hover:ring-primary/40"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Chat-samtaler</span>
            <div className="rounded-lg bg-accent/10 p-2 text-accent">
              <MessageCircle className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-3 text-3xl font-bold text-card-foreground">{chatKpi?.total ?? 0}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {chatKpi && chatKpi.total > 0
              ? `${chatKpi.rate.toFixed(1)}% endte i booking`
              : "Ingen samtaler i perioden"}
          </p>
        </Link>

        <Link
          to="/admin/beskeder"
          className="rounded-xl bg-card p-5 shadow-sm ring-1 ring-border transition hover:ring-primary/40"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Gns. handelsværdi</span>
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <Receipt className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-3 text-3xl font-bold text-card-foreground">
            {salesKpi && salesKpi.count > 0
              ? dkkFormatter.format(salesKpi.current / salesKpi.count)
              : dkkFormatter.format(0)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {salesKpi && salesKpi.count > 0
              ? `Baseret på ${salesKpi.count} ${salesKpi.count === 1 ? "handel" : "handler"}`
              : `Ingen handler i sidste ${chartPeriod} dage`}
          </p>
        </Link>

        <Link
          to="/admin/beskeder"
          className="rounded-xl bg-card p-5 shadow-sm ring-1 ring-border transition hover:ring-primary/40"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Median handelsværdi</span>
            <div className="rounded-lg bg-amber-500/15 p-2 text-amber-600">
              <BarChart3 className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-3 text-3xl font-bold text-card-foreground">
            {salesKpi && salesKpi.median > 0
              ? dkkFormatter.format(salesKpi.median)
              : dkkFormatter.format(0)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {salesKpi && salesKpi.median > 0
              ? "Typisk handel · robust mod outliers"
              : `Ingen handler i sidste ${chartPeriod} dage`}
          </p>
          {salesKpi && salesKpi.median > 0 && (
            <div className="mt-2">{renderDelta(salesKpi.medianDelta)}</div>
          )}
        </Link>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl bg-card p-6 shadow-sm ring-1 ring-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <BarChart3 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-card-foreground">Forespørgsler pr. dag</h2>
                <p className="text-xs text-muted-foreground">Sidste {chartPeriod} dage · {inquiriesPerDay?.reduce((s, d) => s + d.count, 0) ?? 0} i alt</p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5">
                {[7, 30, 90].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setChartPeriod(p as ChartPeriod)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      chartPeriod === p
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {p}d
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                {inquiriesUpdatedAt > 0 && (
                  <p
                    className="text-[10px] text-muted-foreground"
                    title={new Date(inquiriesUpdatedAt).toLocaleString("da-DK")}
                  >
                    Opdateret{" "}
                    {new Date(inquiriesUpdatedAt).toLocaleTimeString("da-DK", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isFetching}
                  title="Hent data igen"
                  aria-label="Genindlæs data"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  title="Kopiér link til denne visning"
                  aria-label="Kopiér link"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Link2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {!inquiriesPerDay ? (
              <div className="h-32 animate-pulse rounded-md bg-muted" />
            ) : (
              (() => {
                const max = Math.max(1, ...inquiriesPerDay.map((d) => d.count));
                return (
                  <div>
                    <div className="flex h-32 items-end gap-[3px]">
                      {inquiriesPerDay.map((d) => {
                        const pct = (d.count / max) * 100;
                        const date = new Date(d.date);
                        const label = `${date.toLocaleDateString("da-DK", { day: "numeric", month: "short" })}: ${d.count} ${d.count === 1 ? "forespørgsel" : "forespørgsler"}`;
                        return (
                          <div
                            key={d.date}
                            title={label}
                            className="group relative flex-1"
                            style={{ height: "100%" }}
                          >
                            <div
                              className={`absolute bottom-0 w-full rounded-sm transition-colors ${d.count > 0 ? "bg-primary/70 group-hover:bg-primary" : "bg-muted group-hover:bg-muted-foreground/30"}`}
                              style={{ height: `${Math.max(pct, 2)}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                      <span>{new Date(inquiriesPerDay[0].date).toLocaleDateString("da-DK", { day: "numeric", month: "short" })}</span>
                      <span>{new Date(inquiriesPerDay[inquiriesPerDay.length - 1].date).toLocaleDateString("da-DK", { day: "numeric", month: "short" })}</span>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Handelsværdi pr. dag/uge — samme periode som forespørgsels-grafen */}
        <div className="rounded-xl bg-card p-6 shadow-sm ring-1 ring-border">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-green-500/15 p-2 text-green-600">
              <DollarSign className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-card-foreground">
                Handelsværdi pr. {salesPerBucket?.[0]?.granularity === "week" ? "uge" : "dag"}
              </h2>
              <p className="text-xs text-muted-foreground">
                Sidste {chartPeriod} dage ·{" "}
                {dkkFormatter.format(salesPerBucket?.reduce((s, b) => s + b.value, 0) ?? 0)} i alt
              </p>
            </div>
          </div>
          <div className="mt-6">
            {!salesPerBucket ? (
              <div className="h-32 animate-pulse rounded-md bg-muted" />
            ) : (
              (() => {
                const max = Math.max(1, ...salesPerBucket.map((b) => b.value));
                const granularityLabel = salesPerBucket[0]?.granularity === "week" ? "Uge" : "";
                return (
                  <div>
                    <div className="flex h-32 items-end gap-[3px]">
                      {salesPerBucket.map((b) => {
                        const pct = (b.value / max) * 100;
                        const date = new Date(b.date);
                        const dateLabel = date.toLocaleDateString("da-DK", { day: "numeric", month: "short" });
                        const prefix = granularityLabel ? `${granularityLabel} fra ${dateLabel}` : dateLabel;
                        const dealLabel = b.count === 1 ? "handel" : "handler";
                        const label = `${prefix}: ${dkkFormatter.format(b.value)} (${b.count} ${dealLabel})`;
                        return (
                          <div
                            key={b.date}
                            title={label}
                            className="group relative flex-1"
                            style={{ height: "100%" }}
                          >
                            <div
                              className={`absolute bottom-0 w-full rounded-sm transition-colors ${b.value > 0 ? "bg-green-500/70 group-hover:bg-green-600" : "bg-muted group-hover:bg-muted-foreground/30"}`}
                              style={{ height: `${Math.max(pct, b.value > 0 ? 4 : 2)}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                      <span>
                        {new Date(salesPerBucket[0].date).toLocaleDateString("da-DK", { day: "numeric", month: "short" })}
                      </span>
                      <span>
                        {new Date(salesPerBucket[salesPerBucket.length - 1].date).toLocaleDateString("da-DK", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-xl bg-card p-6 shadow-sm ring-1 ring-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-orange-500/10 p-2 text-orange-600">
              <Flame className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-card-foreground">Top 5 mest forespurgte biler</h2>
              <p className="text-xs text-muted-foreground">Sidste {chartPeriod} dage</p>
            </div>
          </div>
          <Link
            to="/admin/biler"
            className="text-xs font-medium text-primary hover:underline"
          >
            Se alle biler
          </Link>
        </div>

        <div className="mt-4">
          {topLoading ? (
            <div className="space-y-3 py-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !topInquiredCars || topInquiredCars.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Ingen forespørgsler de sidste {chartPeriod} dage
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {topInquiredCars.map((car, idx) => {
                const label = `${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""}`;
                const thumb = car.images?.[0];
                return (
                  <li key={car.id} className="flex items-center gap-4 py-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                      {idx + 1}
                    </span>
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={label}
                        className="h-12 w-16 shrink-0 rounded-md object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-md bg-muted">
                        <Car className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-card-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{car.year}</p>
                    </div>
                    <Link
                      to="/admin/beskeder"
                      search={{ q: label }}
                      className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/20"
                    >
                      {car.count} {car.count === 1 ? "forespørgsel" : "forespørgsler"}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

    </div>
  );
}
